import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/app/Logo";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/ui";

const QUICK = ["Done", "Waiting on customer response", "In progress", "Push to next Friday"];
const STATUS = { open: "Open", in_progress: "In progress", waiting: "Waiting on others", done: "Done", cancelled: "Cancelled" };

export default function TaskUpdate() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [task, setTask] = useState(null);
  const [error, setError] = useState("");
  const [text, setText] = useState(params.get("q") === "done" ? "Done" : "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get(`/public/task-update/${token}`).then((r) => setTask(r.data)).catch((e) => setError(errMsg(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/public/task-update/${token}`, { text });
      setResult(r.data);
      setTask(r.data.task);
      setText("");
    } catch (err) {
      setError(errMsg(err));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#0B0F19] text-slate-100 flex flex-col items-center px-4 py-10">
      <Link to="/" className="mb-8"><Logo invert /></Link>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0E1424] p-6" data-testid="task-update-page">
        {!task && !error && <Loader2 className="h-5 w-5 animate-spin mx-auto" />}
        {error && !task && <p className="text-sm text-red-300" data-testid="task-update-error">{error}</p>}
        {task && (
          <>
            <div className="text-xs uppercase tracking-wider text-emerald-400 font-semibold">Update your task</div>
            <h1 className="mt-2 text-xl font-semibold" data-testid="task-update-title">{task.title}</h1>
            <p className="mt-1 text-sm text-slate-400" data-testid="task-update-meta">
              {STATUS[task.status]}{task.due_date && ` · due ${fmtDate(task.due_date)}`}{task.entity && ` · ${task.entity}`}{task.related_name && ` · ${task.related_name}`}
            </p>
            {result && (
              <div className="mt-5 flex gap-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/[0.08] p-3.5 text-sm text-emerald-100" data-testid="task-update-result">
                <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" /><span>{result.summary}{result.changes.length > 0 && <span className="block text-xs text-emerald-300/80 mt-1">{result.changes.join(" · ")}</span>}</span>
              </div>
            )}
            <form onSubmit={submit} className="mt-5">
              <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} required
                placeholder="e.g. Completed this morning, or: waiting on customer response, or: push to next Friday"
                className="w-full rounded-lg border border-white/10 bg-[#0B0F19] px-3.5 py-2.5 text-sm outline-none focus:border-emerald-500/60" data-testid="task-update-input" />
              <div className="mt-2 flex flex-wrap gap-2">
                {QUICK.map((q) => <button key={q} type="button" onClick={() => setText(q)} className="rounded-full border border-white/10 px-3 py-1 text-xs text-slate-300 transition-colors hover:border-emerald-500/50 hover:text-white" data-testid={`task-update-quick-${q.split(" ")[0].toLowerCase()}`}>{q}</button>)}
              </div>
              <Button type="submit" disabled={busy || !text.trim()} className="mt-4 w-full" data-testid="task-update-submit">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}Update task
              </Button>
              {error && task && <p className="mt-2 text-xs text-red-300" data-testid="task-update-error">{error}</p>}
            </form>
            {task.history?.length > 0 && (
              <ul className="mt-6 border-t border-white/10 pt-4 space-y-1 text-xs text-slate-400" data-testid="task-update-history">
                {task.history.slice().reverse().map((h, i) => <li key={i}>{fmtDate(h.at)} — {h.event}</li>)}
              </ul>
            )}
          </>
        )}
      </div>
      <p className="mt-6 text-xs text-slate-500">AccruallQ · Corporate Health Operating System</p>
    </div>
  );
}
